import { useCallback, useEffect, useRef, useState } from 'react';

const SCROLL_EDGE_TOLERANCE = 4;

/**
 * State and actions of a horizontal card rail with arrow buttons (Airbnb-style rows): whether
 * there is anything to the left or right, and a page-wise scroll. Shared by the homepage rows
 * (SectionLandingListingRows) and the popular row of the city pages, which used to carry two
 * copies of the same code.
 *
 * SSR-safe: both flags start false, so the server and the first client render agree (arrows
 * idle); the real state is measured after mount and on scroll and resize.
 *
 * @param {number} itemCount re-measure when the number of cards changes
 * @returns {{ scrollerRef: Object, canScrollLeft: boolean, canScrollRight: boolean,
 *   scrollByPage: (direction: -1|1) => () => void }}
 */
export const useHorizontalScroller = itemCount => {
  const scrollerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) {
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > SCROLL_EDGE_TOLERANCE);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - SCROLL_EDGE_TOLERANCE);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollerRef.current;
    if (!el) {
      return undefined;
    }
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState, itemCount]);

  const scrollByPage = direction => () => {
    const el = scrollerRef.current;
    if (el) {
      el.scrollBy({ left: Math.round(el.clientWidth * 0.9) * direction, behavior: 'smooth' });
    }
  };

  return { scrollerRef, canScrollLeft, canScrollRight, scrollByPage };
};
